/**
 * RFC 9457 (`application/problem+json`) is the only error contract the backend exposes, so the
 * whole SPA can rely on one shape. Anything that is not a problem document — a proxy HTML page,
 * a CloudFront error, a dropped connection — is normalised into the same `ApiError` here rather
 * than leaking a second error vocabulary into the feature modules.
 */

export const PROBLEM_JSON_MEDIA_TYPE = 'application/problem+json';

/** A single field-level validation failure, as returned in the `errors` extension member. */
export interface ValidationIssue {
  readonly field: string;
  readonly message: string;
}

/** RFC 9457 problem document. Extension members are preserved under the index signature. */
export interface ProblemDetails {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
  readonly [extension: string]: unknown;
}

export interface ApiErrorInit {
  readonly status: number;
  readonly title: string;
  readonly detail: string | null;
  readonly type: string;
  readonly instance: string | null;
  readonly requestId: string | null;
  readonly issues: readonly ValidationIssue[];
  readonly problem: ProblemDetails | null;
  readonly cause?: unknown;
  /**
   * `detail` was composed by this SPA, in Portuguese, rather than received from the backend —
   * `networkError` below and `features/backoffice/upload.ts`'s own XHR failures are the only two
   * call sites, because a dropped connection or a storage refusal never reaches
   * `normalizeProblemDetails` at all. Defaults to `false`: every backend response is untrusted
   * until proven otherwise, which is the posture the fix for `userMessage` below depends on.
   */
  readonly trustedDetail?: boolean;
}

/** Status used when the request never reached the backend (offline, DNS, CORS, abort). */
export const NETWORK_ERROR_STATUS = 0;

const DEFAULT_MESSAGES: Readonly<Record<number, string>> = {
  0: 'Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.',
  400: 'Não foi possível processar a solicitação.',
  401: 'Sua sessão expirou. Entre novamente para continuar.',
  403: 'Você não tem permissão para realizar esta ação.',
  404: 'Não encontramos o que você procura.',
  409: 'Esta operação conflita com o estado atual do registro.',
  422: 'Revise os campos destacados e tente novamente.',
  429: 'Muitas solicitações em pouco tempo. Aguarde um instante e tente novamente.',
  500: 'Ocorreu um erro no servidor. Tente novamente em instantes.',
  503: 'O serviço está temporariamente indisponível. Tente novamente em instantes.',
};

const FALLBACK_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente.';

/**
 * Short pt-BR headline per status, paired with `DEFAULT_MESSAGES` above. `ProblemDetail.title`
 * (`apps/api/app/core/errors.py`) is always one of "Forbidden", "Not Found", "Internal Server
 * Error"... — English, by the house rule that every identifier is — and every `DomainError`
 * subclass hard-codes it once for every request of that status, with no per-request nuance. A
 * `status` code already picks the one useful thing that title says; this map says it in the
 * language the person looking at the screen reads.
 */
const DEFAULT_TITLES: Readonly<Record<number, string>> = {
  0: 'Falha de conexão',
  400: 'Solicitação inválida',
  401: 'Sessão expirada',
  403: 'Acesso negado',
  404: 'Não encontrado',
  409: 'Conflito',
  422: 'Dados inválidos',
  429: 'Muitas solicitações',
  500: 'Erro no servidor',
  503: 'Serviço indisponível',
};

const FALLBACK_TITLE = 'Não foi possível concluir';

/**
 * A validation issue's `field` is a wire identifier (`quantity`, `valid_from`) rather than
 * prose, so naming it beside Portuguese sentence text is the same kind of mixing the correlation
 * id already gets away with — an opaque token labelled in Portuguese, not a translated one.
 */
function fieldValidationMessage(issues: readonly ValidationIssue[]): string | null {
  const fields = [...new Set(issues.map((issue) => issue.field).filter((field) => field !== ''))];
  if (fields.length === 0) {
    return null;
  }
  return `Revise o preenchimento dos campos indicados antes de tentar novamente: ${fields.join(', ')}.`;
}

export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail: string | null;
  readonly type: string;
  readonly instance: string | null;
  /** Correlates the browser failure with the backend log line for the same request. */
  readonly requestId: string | null;
  readonly issues: readonly ValidationIssue[];
  readonly problem: ProblemDetails | null;
  readonly trustedDetail: boolean;

  constructor(init: ApiErrorInit) {
    super(init.detail ?? init.title, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.status = init.status;
    this.title = init.title;
    this.detail = init.detail;
    this.type = init.type;
    this.instance = init.instance;
    this.requestId = init.requestId;
    this.issues = init.issues;
    this.problem = init.problem;
    this.trustedDetail = init.trustedDetail ?? false;
  }

  /** True when retrying the exact same request could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status === NETWORK_ERROR_STATUS || this.status === 429 || this.status >= 500;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isValidation(): boolean {
    return this.status === 422 || this.issues.length > 0;
  }

  /**
   * pt-BR headline safe to render directly to the user — never `this.title`. That field is the
   * backend's own, always English, always the same four or five words regardless of which
   * request hit it (every 403 is literally "Forbidden"), so it carries no information a `status`
   * code does not already carry, and translating it would be translating nothing lost by not
   * translating it. `this.title` remains on the object for whoever is debugging: it correlates
   * with the server log line under the same `requestId`, which `ErrorState` shows labelled in
   * Portuguese for exactly that support-conversation case.
   */
  get userTitle(): string {
    return DEFAULT_TITLES[this.status] ?? FALLBACK_TITLE;
  }

  /**
   * pt-BR copy safe to render directly to the user.
   *
   * An untrusted `this.detail` is never returned here, with one deliberate exception. Every
   * `DomainError` in `apps/api/app/core/errors.py` composes `detail` in English for an engineer
   * reading a log — "The profile carried by the access token is not allowed on this route." is
   * exactly the kind of sentence that escaped to a screen once already (Sprint 4-13's UI defect
   * sweep, item 1) — so defaulting to it for *any* backend response, in the hope that it might
   * happen to be useful, is the bug, not a feature to preserve. The one case where the backend
   * legitimately knows something this generic list cannot: a 422 whose `errors` extension names
   * the field that failed. Even there, only the field's own identifier is shown
   * (`fieldValidationMessage` above) — never the backend's English sentence about it — because a
   * field name is a technical label like a correlation id, and the sentence around it is prose
   * that must stay in Portuguese.
   *
   * `trustedDetail` is the escape hatch for the two call sites where `detail` never came from the
   * backend at all: `networkError` below and `upload.ts`'s XHR failures compose their own
   * Portuguese sentence in this SPA, and that sentence is the whole reason those functions exist
   * rather than surfacing the browser's or S3's own error text.
   */
  get userMessage(): string {
    if (this.trustedDetail && this.detail !== null) {
      return this.detail;
    }
    if (this.status === 422) {
      const fieldMessage = fieldValidationMessage(this.issues);
      if (fieldMessage !== null) {
        return fieldMessage;
      }
    }
    return DEFAULT_MESSAGES[this.status] ?? FALLBACK_MESSAGE;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Accepts the `errors` extension in both shapes the backend emits: a list of
 * `{ field, message }` objects, and FastAPI's `{ loc: [...], msg }` validation entries.
 */
function readIssues(source: Record<string, unknown>): ValidationIssue[] {
  const raw = source.errors ?? source.invalid_params ?? source.detail;
  if (!Array.isArray(raw)) {
    return [];
  }

  const issues: ValidationIssue[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) {
      continue;
    }

    const message =
      readString(entry, 'message') ?? readString(entry, 'msg') ?? readString(entry, 'reason');
    if (message === null) {
      continue;
    }

    const loc = entry.loc;
    const field =
      readString(entry, 'field') ??
      readString(entry, 'name') ??
      readString(entry, 'pointer') ??
      (Array.isArray(loc) ? loc.filter((part) => typeof part === 'string').join('.') : '');

    issues.push({ field, message });
  }

  return issues;
}

/** Builds an `ApiError` from a failed response whose body has already been parsed. */
export function normalizeProblemDetails(
  response: Pick<Response, 'status' | 'statusText'>,
  body: unknown,
  requestId: string | null,
): ApiError {
  const problem = isRecord(body) ? body : null;

  const status =
    problem !== null && typeof problem.status === 'number' && problem.status >= 100
      ? problem.status
      : response.status;

  const title =
    (problem !== null ? readString(problem, 'title') : null) ??
    (response.statusText.trim() !== '' ? response.statusText : null) ??
    DEFAULT_MESSAGES[status] ??
    FALLBACK_MESSAGE;

  const detail =
    problem !== null
      ? (readString(problem, 'detail') ?? readString(problem, 'message'))
      : typeof body === 'string' && body.trim() !== ''
        ? body
        : null;

  const type = (problem !== null ? readString(problem, 'type') : null) ?? 'about:blank';

  const normalizedProblem: ProblemDetails | null =
    problem === null ? null : ({ ...problem, type, title, status } as ProblemDetails);

  return new ApiError({
    status,
    title,
    detail,
    type,
    instance: problem !== null ? readString(problem, 'instance') : null,
    requestId: (problem !== null ? readString(problem, 'request_id') : null) ?? requestId,
    issues: problem !== null ? readIssues(problem) : [],
    problem: normalizedProblem,
  });
}

/** Builds an `ApiError` for a request that never produced an HTTP response. */
export function networkError(cause: unknown, requestId: string | null): ApiError {
  const aborted = cause instanceof DOMException && cause.name === 'AbortError';

  return new ApiError({
    status: NETWORK_ERROR_STATUS,
    title: aborted ? 'Solicitação cancelada' : 'Falha de conexão',
    detail: aborted
      ? 'A solicitação foi cancelada.'
      : (DEFAULT_MESSAGES[NETWORK_ERROR_STATUS] ?? null),
    type: 'about:blank',
    instance: null,
    requestId,
    issues: [],
    problem: null,
    cause,
    // Composed above, in Portuguese, by this function — never received from the backend, since
    // a network failure never reaches it. `userMessage` may show it verbatim.
    trustedDetail: true,
  });
}
